import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import './OTPVerificationModal.css';

const OTPVerificationModal = ({
  show,
  onHide,
  email,
  onSuccess,
  centerName = 'Penta Community'
}) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputs = useRef([]);

  // Handle resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Focus first input when modal opens
  useEffect(() => {
    if (show && inputs.current[0]) {
      setTimeout(() => inputs.current[0]?.focus(), 100);
    }
  }, [show]);

  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Clear error when user starts typing
    if (error) setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputs.current[index + 1]?.focus();
    }

    // Auto-submit when complete
    const fullCode = newCode.join('');
    if (fullCode.length === 6 && newCode.every(digit => digit)) {
      verifyCode(fullCode);
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }

    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handlePaste(e);
    }
  };

  const handlePaste = async (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData?.getData('text') || await navigator.clipboard.readText();
    const digits = pastedText.replace(/\D/g, '').slice(0, 6);

    if (digits) {
      const newCode = digits.padEnd(6, '').split('');
      setCode(newCode);

      // Focus the next empty input or last input
      const nextEmptyIndex = newCode.findIndex(d => !d);
      if (nextEmptyIndex !== -1) {
        inputs.current[nextEmptyIndex]?.focus();
      } else {
        inputs.current[5]?.focus();
        // Auto-verify if complete
        if (digits.length === 6) {
          verifyCode(digits);
        }
      }
    }
  };

  const verifyCode = async (fullCode) => {
    setLoading(true);
    setError('');

    try {
      console.log('[OTP] Verifying code:', fullCode);

      // Verify OTP with Supabase
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email: email,
        token: fullCode,
        type: 'email'
      });

      if (verifyError) {
        console.error('[OTP] Verification error:', verifyError);
        throw verifyError;
      }

      if (data?.session) {
        console.log('[OTP] ✅ Session created by verifyOtp:', {
          user_id: data.session.user.id,
          email: data.session.user.email,
          access_token_present: !!data.session.access_token,
          refresh_token_present: !!data.session.refresh_token,
          expires_at: data.session.expires_at
        });
        console.log('[OTP] Verification successful, session created');

        // Force React Context to pick up the new session
        await supabase.auth.getSession();
        console.log('[OTP] 🔄 Triggered session refresh to update React Context');

        // Mark as verified in our database
        const { data: dbResult, error: dbError } = await supabase.rpc('complete_otp_verification', {
          p_user_id: data.session.user.id
        });

        if (dbError) {
          console.error('[OTP] Database update error:', dbError);
          // Continue anyway - OTP was verified
        } else {
          console.log('[OTP] Database updated:', dbResult);
        }

        // Call success callback with session
        console.log('[OTP] Calling onSuccess callback with session');
        onSuccess(data.session);
      } else {
        throw new Error('Verification failed - no session created');
      }
    } catch (err) {
      console.error('[OTP] Verification failed:', err);

      if (err.message?.includes('expired')) {
        setError('Code expired. Please request a new one.');
      } else if (err.message?.includes('invalid')) {
        setError('Invalid code. Please check and try again.');
      } else {
        setError('Verification failed. Please try again.');
      }

      // Clear code inputs
      setCode(['', '', '', '', '', '']);
      inputs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setResending(true);
    setError('');

    try {
      console.log('[OTP] Resending code to:', email);

      const { error } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
          shouldCreateUser: false,
          data: {
            service: 'community_services',
            service_label: 'Community Services',
            center_name: centerName,
          }
        }
      });

      if (error) throw error;

      // Set success message and cooldown
      setError(''); // Clear any errors
      setResendCooldown(60); // 60 second cooldown

      // Show success feedback
      const tempMsg = document.createElement('div');
      tempMsg.className = 'otp-resend-success';
      tempMsg.textContent = 'New code sent to your email!';
      document.querySelector('.otp-modal-body')?.appendChild(tempMsg);
      setTimeout(() => tempMsg.remove(), 3000);

      // Clear and focus first input
      setCode(['', '', '', '', '', '']);
      inputs.current[0]?.focus();
    } catch (err) {
      console.error('[OTP] Resend failed:', err);
      setError('Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal-overlay otp-verification-modal" onClick={(e) => {
      // Don't allow closing by clicking outside - verification is required
      e.stopPropagation();
    }}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Verify Your Email</h2>
          {/* Close button removed - verification is required */}
        </div>

        <div className="modal-body otp-modal-body">
          <div className="text-center">
          <p className="mb-4">
            We've sent a 6-digit verification code to<br />
            <strong>{email}</strong>
          </p>

          <div className="otp-input-container">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={el => inputs.current[index] = el}
                type="text"
                maxLength="1"
                value={digit}
                onChange={e => handleCodeChange(index, e.target.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className={`otp-input ${error ? 'error' : ''}`}
                disabled={loading}
                placeholder="•"
                autoComplete="off"
                inputMode="numeric"
                pattern="[0-9]"
              />
            ))}
          </div>

          {error && (
            <div className="alert alert-danger mt-3 mb-0">
              {error}
            </div>
          )}

          {loading && (
            <div className="mt-3">
              <span className="spinner-small"></span>
              Verifying code...
            </div>
          )}

          <div className="mt-4">
            <button
              type="button"
              onClick={resendCode}
              disabled={resending || resendCooldown > 0}
              className="btn-link"
            >
              {resending ? (
                <>
                  <span className="spinner-small"></span>
                  Sending...
                </>
              ) : resendCooldown > 0 ? (
                `Resend code in ${resendCooldown}s`
              ) : (
                "Didn't receive the code? Resend"
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="modal-footer" style={{ justifyContent: 'center' }}>
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            const fullCode = code.join('');
            if (fullCode.length === 6) {
              verifyCode(fullCode);
            }
          }}
          disabled={loading || code.join('').length !== 6}
        >
          {loading ? 'Verifying...' : 'Verify Email'}
        </button>
      </div>
    </div>
    </div>
  );
};

export default OTPVerificationModal;